import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import {
  extractUniqueNames,
  extractValue,
  extractValueByNodeName,
  findResponseByIdentifier,
  makeCustomData,
} from "components/dashboard/charts/sankey-chart/sankey-utils";
import {
  FormalSankeyConfig,
  FormalSankeyData,
  FormalSankeyLink,
  NodeValues,
  SankeyDiagramGraph,
  SideCounter,
} from "components/dashboard/charts/sankey-chart/sankey-types";
import { ZoneId } from "@js-joda/core";

export const transformDataToSankeyData = (
  graph: SankeyDiagramGraph,
  data: DatasetDataInfoResponse[],
  configDecimalPlace: number | undefined,
  configPercentagesDecimalPlaces = 1,
  tz: ZoneId
): FormalSankeyConfig => {
  const nodeIndices = graph.nodes.reduce(
    (acc, node, index) => {
      acc[node.id] = index;
      return acc;
    },
    {} as Record<string, number>
  );

  const sideCounter = graph.nodes.map(
    n =>
      ({
        nodeName: n.id,
        type: graph.links.filter(e => e.target === n.id).length === 0 ? "SOURCE" : "TARGET",
      }) as SideCounter
  );

  let nodeValues: NodeValues[] = sideCounter.map(e => ({
    nodeName: e.nodeName,
    value: 0,
    decimalPlace: 2,
    unit: "",
  }));

  const tempLinks: FormalSankeyLink[] = graph.links.map(link => {
    const res = findResponseByIdentifier(data, link.identifier);
    const sourceIndex = nodeIndices[link.source];
    const targetIndex = nodeIndices[link.target];

    sideCounter.forEach(n => {
      nodeValues = nodeValues.map(node => {
        if (
          (n.nodeName === link.source && n.type === "SOURCE" && node.nodeName === n.nodeName) ||
          (n.nodeName === link.target && n.type === "TARGET" && node.nodeName === n.nodeName)
        ) {
          return {
            nodeName: n.nodeName,
            value: (node.value + (res?.data[0]?.value || 0)) / (link.customDivider || 1),
            decimalPlace: res?.decimalPlaces || 2,
            unit: link.customUnit ?? (res?.unit as string) ?? "",
          };
        }
        return node;
      });
    });

    return {
      source: sourceIndex,
      target: targetIndex,
      value: res?.data[0]?.value ? res?.data[0]?.value / (link.customDivider || 1) : undefined,
      date: res?.data[0]?.key || "",
      title: res?.title || "",
      linkName: `${graph.nodes[sourceIndex].title} -> ${graph.nodes[targetIndex].title}`,
      unit: link.customUnit ?? (res?.unit as string) ?? "",
      showingValue: extractValue(
        res?.data[0]?.value ? res?.data[0]?.value / (link.customDivider || 1) : 0,
        configDecimalPlace || res?.decimalPlaces || 2
      ),
    } as FormalSankeyLink;
  });

  let referenceValue = 0;
  nodeValues.forEach(n => {
    graph.nodes.forEach(g => {
      if (g.id === n.nodeName && g.reference) {
        referenceValue += n.value;
      }
    });
  });
  const nodeNames = extractUniqueNames(
    graph,
    nodeValues,
    referenceValue,
    configDecimalPlace,
    configPercentagesDecimalPlaces
  );
  const links: FormalSankeyData = {
    value: tempLinks.map(e => e.value),
    source: tempLinks.map(e => e.source),
    target: tempLinks.map(e => e.target),
    date: tempLinks.map(e => e.date),
    title: tempLinks.map(e => e.title),
    linkName: tempLinks.map(e => e.linkName),
    customdata: tempLinks.map(e =>
      makeCustomData(
        e.showingValue,
        e.unit,
        e.title,
        e.date,
        graph.nodes[e.source].title,
        extractValueByNodeName(nodeValues, graph.nodes[e.source].id),
        tz
      )
    ),
  };

  return { nodeNames, links };
};
